/* eslint-disable max-lines */
import { appointmentKeys } from './appointmentKeys';

// Recurrence handlers commit async after responding, so those paths delay to
// avoid pre-commit refetch. The plain update_v2 handler commits first — no delay.
const RECURRENCE_INVALIDATION_DELAY_MS = 1000;

const toEpoch = (d) => (d == null ? null : d instanceof Date ? d.getTime() : new Date(d).getTime());

// Coerce like the key factory, but return null instead of throwing on garbage.
const toNumericId = (id) => {
  if (id == null) return null;
  const n = Number(id);
  return Number.isFinite(n) ? n : null;
};

// Dates → epoch ms. Skips null; an unparseable date signals a broad fallback.
const collectMs = (dates) => {
  const msList = [];
  let hadInvalid = false;
  for (const d of dates) {
    if (d == null) continue;
    const ms = toEpoch(d);
    if (Number.isFinite(ms)) msList.push(ms);
    else {
      hadInvalid = true;
      console.error(`collectMs: unparseable date ${String(d)}; broad calendar invalidation`);
    }
  }
  return { msList, hadInvalid };
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Predicate-based calendar invalidation. Only invalidates cached calendar ranges
// whose [fromMs, toMs] window contains at least one of the affected dates.
// Falls back to a prefix invalidation when no dates are supplied or one is bad.
const invalidateCalendarTouching = (qc, affectedDates) => {
  const { msList: eventMsList, hadInvalid } = collectMs(affectedDates || []);
  if (hadInvalid || eventMsList.length === 0) {
    return qc.invalidateQueries({ queryKey: appointmentKeys.calendar() });
  }
  return qc.invalidateQueries({
    predicate: (q) => {
      // Short-circuit FIRST: most cached queries in the app are unrelated.
      if (q.queryKey[0] !== 'appointments') return false;
      if (q.queryKey[1] !== 'calendar') return false;
      const params = q.queryKey[2];
      if (!params || typeof params !== 'object') return false;
      const { fromMs, toMs } = params;
      if (typeof fromMs !== 'number' || typeof toMs !== 'number') return false;
      for (const ms of eventMsList) if (fromMs <= ms && ms <= toMs) return true;
      return false;
    }
  });
};

// `refetchType: 'active'` skips background/inactive queue queries on the spot;
// they refetch on next mount when fresh. Cuts cascade cost for deep pagination.
const invalidateQueue = (qc) =>
  qc.invalidateQueries({ queryKey: appointmentKeys.queue(), refetchType: 'active' });

const invalidatePatient = (qc, patientId) => {
  const pid = toNumericId(patientId);
  if (pid == null) {
    if (patientId != null)
      console.error(`invalidatePatient: unparseable patientId ${String(patientId)}`);
    return Promise.resolve();
  }

  return Promise.all([
    qc.invalidateQueries({ queryKey: appointmentKeys.byPatientListAll(pid) }),
    qc.invalidateQueries({ queryKey: appointmentKeys.byPatientOverview(pid) }),
    // Infinite queries refetch every rendered page sequentially. Mark stale
    // without immediate refetch — the picker reopens with fresh data on mount.
    qc.invalidateQueries({
      queryKey: appointmentKeys.byPatientInfiniteAll(pid),
      refetchType: 'none'
    })
  ]);
};

const invalidateInvoices = (qc, id) => {
  const numericId = toNumericId(id);
  if (numericId == null) return Promise.resolve();
  return qc.invalidateQueries({ queryKey: appointmentKeys.invoices(numericId) });
};

const invalidatePerId = (qc, id) => {
  const numericId = toNumericId(id);
  if (numericId == null) {
    if (id != null) console.error(`invalidatePerId: unparseable id ${String(id)}`);
    return Promise.resolve();
  }
  return Promise.all([
    qc.invalidateQueries({ queryKey: appointmentKeys.detail(numericId) }),
    qc.invalidateQueries({ queryKey: appointmentKeys.detailV2(numericId) }),
    qc.invalidateQueries({ queryKey: appointmentKeys.timeline(numericId) }),
    qc.invalidateQueries({ queryKey: appointmentKeys.activities(numericId) })
  ]);
};

const removePerId = (qc, id) => {
  const numericId = toNumericId(id);
  if (numericId == null) {
    if (id != null) console.error(`removePerId: unparseable id ${String(id)}`);
    return;
  }
  qc.removeQueries({ queryKey: appointmentKeys.detail(numericId) });
  qc.removeQueries({ queryKey: appointmentKeys.detailV2(numericId) });
  qc.removeQueries({ queryKey: appointmentKeys.timeline(numericId) });
  qc.removeQueries({ queryKey: appointmentKeys.activities(numericId) });
  qc.removeQueries({ queryKey: appointmentKeys.invoices(numericId) });
};

// ─── Per-mutation helpers ────────────────────────────────────────────────────

export const invalidateForCreate = (qc, { id, patientId, startsAt } = {}) =>
  Promise.all([
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc),
    qc.invalidateQueries({ queryKey: appointmentKeys.pending() })
  ]);

export const invalidateForUpdate = (
  qc,
  { id, patientId, oldStartsAt, newStartsAt, locationChanged, pricingChanged, statusChanged } = {}
) => {
  const tasks = [invalidatePerId(qc, id), invalidatePatient(qc, patientId)];
  const timeChanged =
    oldStartsAt != null && newStartsAt != null && toEpoch(oldStartsAt) !== toEpoch(newStartsAt);
  if (timeChanged || locationChanged) {
    tasks.push(invalidateCalendarTouching(qc, [oldStartsAt, newStartsAt]));
  }
  if (statusChanged || timeChanged) tasks.push(invalidateQueue(qc));
  if (pricingChanged) tasks.push(invalidateInvoices(qc, id));
  return Promise.all(tasks);
};

// `seriesScope`: 'this' → immediate (single instance). 'following' | 'all' →
// backend returns only the root id, so we fall back to patient + calendar and
// wait for the recurrence transaction to commit before invalidating.
export const invalidateForReschedule = async (
  qc,
  { id, patientId, oldStartsAt, newStartsAt, seriesScope = 'this' } = {}
) => {
  if (seriesScope !== 'this') await delay(RECURRENCE_INVALIDATION_DELAY_MS);
  return Promise.all([
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [oldStartsAt, newStartsAt]),
    invalidateQueue(qc)
  ]);
};

// Entity-create: detach one occurrence from a recurring series. Backend creates
// a new row in a transaction that commits after responding, so delay applies.
// `oldStartsAt` covers the reschedule case — moving the occurrence must also
// refresh the calendar range it left behind, not just the new slot.
export const invalidateForEntityCreate = async (qc, { oldStartsAt, ...args } = {}) => {
  await delay(RECURRENCE_INVALIDATION_DELAY_MS);
  const tasks = [invalidateForCreate(qc, args)];
  if (oldStartsAt != null) tasks.push(invalidateCalendarTouching(qc, [oldStartsAt]));
  return Promise.all(tasks);
};

export const invalidateForCancel = (qc, { id, patientId, startsAt, wasPending } = {}) => {
  const tasks = [
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc)
  ];
  if (wasPending) tasks.push(qc.invalidateQueries({ queryKey: appointmentKeys.pending() }));
  return Promise.all(tasks);
};

export const invalidateForRestore = (qc, { id, patientId, startsAt } = {}) =>
  Promise.all([
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc)
  ]);

export const invalidateForDelete = (qc, { id, patientId, startsAt } = {}) => {
  removePerId(qc, id);
  return Promise.all([
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc)
  ]);
};

export const invalidateForComplete = (qc, { id, patientId, chargeChanged } = {}) => {
  const tasks = [invalidatePerId(qc, id), invalidatePatient(qc, patientId), invalidateQueue(qc)];
  if (chargeChanged) tasks.push(invalidateInvoices(qc, id));
  return Promise.all(tasks);
};

export const invalidateForAccept = (qc, { id, patientId, startsAt } = {}) =>
  Promise.all([
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc),
    qc.invalidateQueries({ queryKey: appointmentKeys.pending() })
  ]);

export const invalidateForDeclinePending = (qc, { id, patientId, startsAt } = {}) => {
  removePerId(qc, id);
  return Promise.all([
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc),
    qc.invalidateQueries({ queryKey: appointmentKeys.pending() })
  ]);
};

// no-show fee flips status → no_show and adds an invoice, so everything moves.
export const invalidateForNoShowFee = (qc, { id, patientId, startsAt } = {}) =>
  Promise.all([
    invalidatePerId(qc, id),
    invalidatePatient(qc, patientId),
    invalidateCalendarTouching(qc, [startsAt]),
    invalidateQueue(qc),
    invalidateInvoices(qc, id)
  ]);

export const invalidateForApplyInsurance = (qc, { id } = {}) =>
  Promise.all([invalidatePerId(qc, id), invalidateInvoices(qc, id)]);

// Bulk action helper. Dedupes shared work across rows so per-id work runs
// once per unique id, patient work once per unique patientId, and shared
// work (calendar, queue, pending) runs once total.
export const invalidateBulk = (qc, rows, action) => {
  const ids = new Set();
  const patients = new Set();
  const dates = [];
  let anyWasPending = false;
  const rowList = Array.isArray(rows) ? rows : [];
  for (const r of rowList) {
    if (r?.id != null) ids.add(r.id);
    if (r?.patientId != null) patients.add(r.patientId);
    if (r?.startsAt) dates.push(r.startsAt);
    if (r?.wasPending) anyWasPending = true;
  }
  // No patientId on any row → can't scope the patient refresh; full fallback.
  // invalidateAllAppointments only hits the ['appointments'] root, so refresh
  // each id's per-id queries too — activity logs live outside that root.
  if (rowList.length > 0 && patients.size === 0) {
    return Promise.all([
      ...[...ids].map((id) => invalidatePerId(qc, id)),
      invalidateAllAppointments(qc)
    ]);
  }
  const tasks = [];
  for (const id of ids) {
    tasks.push(invalidatePerId(qc, id));
    if (action === 'noShow' || action === 'complete') tasks.push(invalidateInvoices(qc, id));
  }
  for (const patientId of patients) tasks.push(invalidatePatient(qc, patientId));
  tasks.push(invalidateCalendarTouching(qc, dates));
  tasks.push(invalidateQueue(qc));
  if (action === 'cancel' && anyWasPending) {
    tasks.push(qc.invalidateQueries({ queryKey: appointmentKeys.pending() }));
  }
  return Promise.all(tasks);
};

// Escape hatch: nuke every appointment query. Use only when the response is
// too opaque to figure out what changed, or to recover from a known-bad cache.
export const invalidateAllAppointments = (qc) =>
  qc.invalidateQueries({ queryKey: appointmentKeys.all });

export const __testables = {
  RECURRENCE_INVALIDATION_DELAY_MS,
  invalidateCalendarTouching,
  invalidatePatient,
  invalidatePerId,
  removePerId
};
